import { type Accessor, createSignal, onCleanup } from "solid-js";

interface UsePanelResizeOptions {
  /** Get the initial/current size of the panel */
  getSize: () => number;
  /** Callback to update the panel size */
  onResize: (newSize: number) => void;
  /** Direction the panel grows when dragging. 'right' = navbar (drag right to grow), 'left' = side panel (drag left to grow) */
  direction: "left" | "right";
  /** Optional: Check if resize should be disabled (e.g., panel is collapsed) */
  isDisabled?: () => boolean;
  /** Optional: Threshold below which to trigger collapse (e.g. side panel dragged small) */
  collapseThreshold?: number;
  /** Optional: Callback when dragged below collapse threshold */
  onCollapseThreshold?: () => void;
  /** Optional: For direction "left" only. When the opposite (main) panel would be smaller than this, trigger onPromoteThreshold (e.g. promote side to main). */
  mainMinThreshold?: number;
  /** Optional: Get width of the container that holds both panels (required when using mainMinThreshold). */
  getContainerWidth?: () => number;
  /** Optional: Callback when main is squeezed (drag left past mainMinThreshold); use for "promote side to main". */
  onPromoteThreshold?: () => void;
}

interface UsePanelResizeReturn {
  /** Whether the user is currently dragging to resize */
  isResizing: Accessor<boolean>;
  /** Whether the user is hovering over the resize handle */
  isHovering: Accessor<boolean>;
  /** Set hover state - call on mouseEnter/mouseLeave */
  setHovering: (hovering: boolean) => void;
  /** Start resize drag - call on mouseDown of resize handle */
  handleStart: (e: MouseEvent) => void;
}

/**
 * Hook for handling panel resize drag interactions.
 * Consolidates the common pattern of tracking drag state, document listeners, and cursor management.
 */
export function usePanelResize(options: UsePanelResizeOptions): UsePanelResizeReturn {
  const {
    getSize,
    onResize,
    direction,
    isDisabled,
    collapseThreshold,
    onCollapseThreshold,
    mainMinThreshold,
    getContainerWidth,
    onPromoteThreshold,
  } = options;

  const [isResizing, setIsResizing] = createSignal(false);
  const [isHovering, setIsHovering] = createSignal(false);

  let startX = 0;
  let startWidth = 0;

  const handleMove = (e: MouseEvent) => {
    if (!isResizing()) return;

    // Calculate delta based on direction
    // 'left': panel grows to the left (side panel), so subtract delta
    // 'right': panel grows to the right (navbar), so add delta
    const delta = direction === "left" ? startX - e.clientX : e.clientX - startX;
    const newWidth = startWidth + delta;

    // Check if we should collapse (side panel dragged small → threshold)
    if (collapseThreshold !== undefined && newWidth < collapseThreshold && onCollapseThreshold) {
      onCollapseThreshold();
      handleEnd();
      return;
    }

    // When dragging left, side panel grows. If main area would be squeezed below mainMinThreshold, promote side to main.
    if (
      direction === "left" &&
      mainMinThreshold !== undefined &&
      getContainerWidth !== undefined &&
      onPromoteThreshold
    ) {
      const containerW = getContainerWidth();
      if (containerW > 0 && containerW - newWidth < mainMinThreshold) {
        onPromoteThreshold();
        handleEnd();
        return;
      }
    }

    onResize(newWidth);
  };

  const handleEnd = () => {
    setIsResizing(false);
    document.removeEventListener("mousemove", handleMove);
    document.removeEventListener("mouseup", handleEnd);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  const handleStart = (e: MouseEvent) => {
    if (isDisabled?.()) return;

    e.preventDefault();
    setIsResizing(true);
    startX = e.clientX;
    startWidth = getSize();

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const setHovering = (hovering: boolean) => {
    // Don't clear hover state while resizing (keeps indicator visible)
    if (!hovering && isResizing()) return;
    setIsHovering(hovering);
  };

  // Cleanup on unmount
  onCleanup(() => {
    document.removeEventListener("mousemove", handleMove);
    document.removeEventListener("mouseup", handleEnd);
  });

  return {
    isResizing,
    isHovering,
    setHovering,
    handleStart,
  };
}
